import { useEffect, useState } from 'react';

/**
 * Add event listener on scroll and monitor the boundary of a target element
 * The event listener is removed once the target is detected inside the viewport
 *
 * @param offset - Define by how many pixel the viewport height should be extend
 * useful if you want perform action before the target is within the viewport.
 * @returns ref - a mutable ref object to be added to the target component
 * isInsideViewport - a boolean that it's set to true when target element is within the viewport
 */
const useDetectTargetIsInsideViewport = ({
    ref,
    offset = 200,
}: {
    ref: React.MutableRefObject<HTMLDivElement | null>;
    offset?: number;
}) => {
    const [isInsideViewport, setIsInsideViewport] = useState(false);

    useEffect(() => {
        const checkIfRefIsOutsideViewport = () => {
            const rect = ref?.current?.getBoundingClientRect();
            const topOfWindow = 0;
            const topOfWindowWithOffset = offset * -1;
            const bottomOfWindow = window.innerHeight;
            const bottomOfZoneWithOffset = window.innerHeight + offset;

            if (rect) {
                const topIsInside =
                    rect.top >= topOfWindowWithOffset &&
                    rect.top <= bottomOfZoneWithOffset;
                const bottomIsInside =
                    rect.bottom >= topOfWindowWithOffset &&
                    rect.bottom <= bottomOfZoneWithOffset;
                const topIsOverAndBottomBelow =
                    rect.top <= topOfWindow && rect.bottom >= bottomOfWindow;

                if (topIsInside || bottomIsInside || topIsOverAndBottomBelow) {
                    setIsInsideViewport(true);
                }
            }
        };

        checkIfRefIsOutsideViewport();

        if (!isInsideViewport) {
            window.addEventListener('scroll', checkIfRefIsOutsideViewport);
        }

        return () => {
            window.removeEventListener('scroll', checkIfRefIsOutsideViewport);
        };
    }, [ref, isInsideViewport, offset]);

    return isInsideViewport;
};

export default useDetectTargetIsInsideViewport;
